import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Star, Award, CheckCircle2, ChevronDown, Clock, Zap,
  Smartphone, Briefcase, Target, TrendingUp, GraduationCap,
  Sparkles, Quote, Code, Database, Rocket, Globe, Terminal, Cpu, X
} from 'lucide-react';
import emailjs from '@emailjs/browser';
import Section from '../../components/common/Section';

import Button from '../../components/common/Button';
const curriculum = [
  {
    phase: 'Programming Foundations',
    weeks: 'Weeks 1–2',
    color: '#bebd19',
    modules: [
      { week: 'WK 1', title: 'Programming Thinking & Dart/JS Basics', desc: 'Variables, functions, loops, and object-oriented fundamentals — the building blocks of app development.' },
      { week: 'WK 2', title: 'Development Environment Setup', desc: 'Install Flutter/React Native, configure emulators, and run your first "Hello World" app on device.' },
    ],
  },
  {
    phase: 'Cross-Platform App Dev',
    weeks: 'Weeks 3–5',
    color: '#bebd19',
    modules: [
      { week: 'WK 3', title: 'Flutter UI Fundamentals', desc: "Widgets, layouts, navigation, and building responsive screens with Flutter's widget system." },
      { week: 'WK 4', title: 'React Native Essentials', desc: 'Components, StyleSheet, navigation with React Navigation, and cross-platform state management.' },
      { week: 'WK 5', title: 'State Management & Data Flow', desc: 'Provider, Riverpod, or Zustand — manage app state, handle user input, and persist local data.' },
    ],
  },
  {
    phase: 'AI-Assisted Coding',
    weeks: 'Weeks 6–7',
    color: '#bebd19',
    modules: [
      { week: 'WK 6', title: 'Cursor AI & GitHub Copilot', desc: 'Write code 10x faster with AI pair-programming — autocompletion, refactoring, and debugging assistance.' },
      { week: 'WK 7', title: 'Claude Code & AI Debugging', desc: 'Use AI to understand error messages, generate boilerplate, write tests, and optimize performance.' },
    ],
  },
  {
    phase: 'Backend & APIs',
    weeks: 'Weeks 8–10',
    color: '#bebd19',
    modules: [
      { week: 'WK 8', title: 'Firebase / Supabase Fundamentals', desc: 'Authentication, real-time database, and cloud storage — set up a backend in minutes.' },
      { week: 'WK 9', title: 'REST APIs & Integration', desc: 'Consume third-party APIs, handle authentication tokens, and manage network state in your app.' },
      { week: 'WK 10', title: 'Backend Logic & Cloud Functions', desc: 'Write serverless functions, process payments, and handle push notifications.' },
    ],
  },
  {
    phase: 'Real Client Simulation',
    weeks: 'Weeks 11–13',
    color: '#bebd19',
    modules: [
      { week: 'WK 11', title: 'Capstone Planning & Architecture', desc: "Design your app's architecture, plan features, and set up a scalable project structure." },
      { week: 'WK 12', title: 'Capstone Development Sprint', desc: 'Build core features: authentication, data flows, UI screens — with weekly code reviews.' },
      { week: 'WK 13', title: 'Capstone Polish & Testing', desc: 'Performance optimization, bug fixes, UI polish, and preparing for Play Store / App Store submission.' },
    ],
  },
  {
    phase: 'Launch & Career',
    weeks: 'Week 14',
    color: '#bebd19',
    modules: [
      { week: 'WK 14', title: 'App Launch & Portfolio Presentation', desc: 'Submit your app to the store, present your work, and get feedback from mentors and peers.' },
    ],
  },
];

const aiTools = [
  { name: 'Flutter / React Native', desc: 'Cross-platform frameworks to build for Android, iOS, and web from a single codebase.', icon: Globe },
  { name: 'Cursor AI & GitHub Copilot', desc: 'AI pair-programming for faster, cleaner code — autocompletion, refactoring, and test generation.', icon: Cpu },
  { name: 'Claude Code', desc: 'AI-powered debugging, code explanation, and performance optimization assistant.', icon: Terminal },
  { name: 'Firebase / Supabase', desc: 'Backend-as-a-service for auth, database, storage, and serverless functions.', icon: Database },
  { name: 'Figma-to-Code Plugins', desc: 'Convert design screens directly into Flutter/React Native code with AI-powered plugins.', icon: Zap },
];

const capstoneProjects = [
  { title: 'Food Delivery / Booking App Clone', desc: 'Build a Swiggy/Zomato-style app with menus, cart, ordering flow, and real-time order tracking — a proven portfolio showpiece.' },
  { title: 'Social / Community App with Chat', desc: 'Create a community platform with profiles, posts, comments, and real-time chat — demonstrating social features and WebSocket integration.' },
  { title: 'Full SaaS App with Login & Payments', desc: 'Ship a subscription-based app with authentication, Stripe payments, user dashboard, and admin panel — a complete SaaS prototype.' },
];

const audience = [
  { num: '01', title: 'Fresh Graduates & Career Switchers', desc: 'No coding experience needed. Start from zero and ship your first app in 14 weeks with AI-assisted learning.' },
  { num: '02', title: 'Designers Adding Development', desc: 'Already know UI/UX? Learn to build your own designs — a massive advantage in freelance and startup environments.' },
  { num: '03', title: 'Entrepreneurs & Founders', desc: 'Turn your app idea into a working prototype without hiring a development team — validate before you invest.' },
  { num: '04', title: 'Marketing Professionals', desc: 'Add app development to your toolkit — build internal tools, client apps, or your own SaaS products.' },
];

const testimonials = [
  { name: 'Vikram Joshi', role: 'Mobile Developer at Infosys', text: 'I had zero coding experience before this course. The AI tools made learning to code feel natural — I shipped my first app in week 8 and got hired by week 16.' },
  { name: 'Neha Gupta', role: 'Freelance App Developer', text: 'The Cursor AI + Flutter combo I learned here lets me build client apps in half the time. The capstone project alone was worth the entire course fee.' },
  { name: 'Arjun Singh', role: 'Founder, FreshBite App', text: 'Built and launched my food delivery MVP using the capstone project structure. Secured seed funding 2 months after completing the course.' },
];

const comparisonData = [
  { feature: 'AI-assisted coding throughout', pyrus: true, udemy: false, bootcamp: false },
  { feature: 'Ship a real app to Play Store', pyrus: true, udemy: false, bootcamp: false },
  { feature: 'Live mentorship from working devs', pyrus: true, udemy: false, bootcamp: true },
  { feature: 'Small batch size (30 seats)', pyrus: true, udemy: false, bootcamp: false },
  { feature: 'Backend + API integration included', pyrus: true, udemy: false, bootcamp: true },
  { feature: 'Certificate + LinkedIn credential', pyrus: true, udemy: true, bootcamp: true },
  { feature: 'Access to session recordings', pyrus: true, udemy: true, bootcamp: false },
  { feature: 'Placement support & freelance opportunities', pyrus: true, udemy: false, bootcamp: true },
];

const faqs = [
  { q: 'Do I need any coding experience to join?', a: 'No. The first two weeks are designed for complete beginners. We start with programming fundamentals before moving into app frameworks.' },
  { q: 'Which platform will I build for — Android, iOS, or both?', a: 'Both. You\'ll learn cross-platform frameworks (Flutter or React Native) that let you build for Android, iOS, and even web from a single codebase.' },
  { q: 'Is this online, offline, or both?', a: 'Live online sessions with the option to attend in-person at our Pune studio for select sessions and the final capstone review.' },
  { q: 'What if I miss a live session?', a: 'Every session is recorded and shared with the cohort, so you can catch up without falling behind on the weekly project milestones.' },
  { q: 'Will I actually publish an app?', a: 'Yes. The capstone includes preparing your app for Play Store / App Store submission. Many students publish their capstone apps during the course.' },
  { q: 'Is there a payment plan?', a: 'Yes — No-cost EMI options are available. You can also pay in 2 installments: 60% at enrollment, 40% by week 7.' },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: 'easeOut' },
  }),
};

const AppDevelopment = () => {
  const [openFaq, setOpenFaq] = useState(0);
  const [showMobileCta, setShowMobileCta] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [form, setForm] = useState({ name: '', email: '', phone: '', qualification: '' });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [sending, setSending] = useState(false);

  const batches = [
    { date: 'August 10, 2026', seatsLeft: 18 },
    { date: 'September 8, 2026', seatsLeft: 30 },
  ];

  const validateField = (name, value) => {
    let msg = '';
    switch (name) {
      case 'name':
        if (!value.trim()) msg = 'Full name is required';
        else if (value.trim().length < 2) msg = 'Name must be at least 2 characters';
        break;
      case 'email':
        if (!value.trim()) msg = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) msg = 'Please enter a valid email';
        break;
      case 'phone':
        if (!value.trim()) msg = 'Mobile number is required';
        else if (!/^[6-9]\d{9}$/.test(value.replace(/\s/g, ''))) msg = 'Please enter a valid 10-digit Indian mobile number';
        break;
      case 'qualification':
        if (!value) msg = 'Please select your qualification';
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [name]: msg }));
    return msg;
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    validateField(name, value);
  };

  const handleFormBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    validateField(name, value);
  };

  const handleDateChange = (e) => {
    const val = e.target.value;
    setSelectedDate(val);
    const msg = val ? '' : 'Please select a start date';
    setErrors((prev) => ({ ...prev, date: msg }));
    setTouched((prev) => ({ ...prev, date: true }));
  };

  const handleDateBlur = () => {
    setTouched((prev) => ({ ...prev, date: true }));
    const msg = selectedDate ? '' : 'Please select a start date';
    setErrors((prev) => ({ ...prev, date: msg }));
  };

  const validateAll = () => {
    const nameErr = validateField('name', form.name);
    const emailErr = validateField('email', form.email);
    const phoneErr = validateField('phone', form.phone);
    const qualErr = validateField('qualification', form.qualification);
    const dateErr = selectedDate ? '' : 'Please select a start date';
    setErrors((prev) => ({ ...prev, date: dateErr }));
    setTouched({ name: true, email: true, phone: true, qualification: true, date: true });
    return !nameErr && !emailErr && !phoneErr && !qualErr && !dateErr;
  };

  const handleSubmit = async () => {
    if (!validateAll()) return;
    setSending(true);
    try {
      await emailjs.send(
        'service_k58equi',
        'template_yzpafsl',
        {
          from_name: form.name,
          from_email: form.email,
          phone: form.phone,
          qualification: form.qualification,
          start_date: selectedDate,
          course: 'App Development with AI',
        },
        'OPcmD1RuZ7WBh3Bwj'
      );
      alert(`Thank you, ${form.name}! Your seat has been reserved for App Development with AI (${selectedDate}). We'll contact you shortly.`);
    } catch (err) {
      console.error('EmailJS Error:', err);
      alert('Something went wrong while sending your details. Please try again.');
    }
    setSending(false);
    setShowModal(false);
    setForm({ name: '', email: '', phone: '', qualification: '' });
    setSelectedDate('');
    setErrors({});
    setTouched({});
  };

  const inputClass = (field) =>
    `w-full border rounded-lg p-3 text-gray-900 focus:outline-none ${
      errors[field] && touched[field] ? 'border-red-400 focus:border-red-500' : 'border-gray-300 focus:border-[#bebd19]'
    }`;

  useEffect(() => {
    const handleScroll = () => {
      setShowMobileCta(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <Helmet>
        <title>Build & Launch Your Own App in 14 Weeks — Using AI-Assisted Development | PyrusMedia Academy</title>
        <meta name="description" content="Learn to build real Android/iOS/cross-platform apps using Flutter, React Native, and AI coding tools. 14-week cohort with live projects and placement support." />
      </Helmet>

      {/* HERO */}
      <section className="pt-32 pb-20 bg-white text-center relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#bebd19]/20 rounded-full blur-[100px] pointer-events-none" />
        <div className="container mx-auto px-4 relative z-10 max-w-4xl">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 bg-[#bebd19]/10 text-[#bebd19] rounded-full mb-6 border border-[#bebd19]/20"
          >
            <Code className="w-4 h-4" />
            14-Week Live Cohort
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight"
          >
            Build & Launch Your Own App in 14 Weeks — Using AI-Assisted Development
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg text-gray-500 max-w-2xl mx-auto mb-8"
          >
            Learn to build real Android/iOS/cross-platform apps faster using Flutter, React Native, and AI coding tools — no prior coding experience needed.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-wrap justify-center gap-4 mb-12"
          >
            <Button onClick={() => setShowModal(true)} className="px-8 py-4 text-base">
              Reserve Your Seat
            </Button>
            <Button to="#curriculum" variant="outline" className="px-8 py-4 text-base">
              See the Curriculum
            </Button>
          </motion.div>
          {/* Trust strip */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-wrap justify-center gap-6 md:gap-10 pt-8 border-t border-gray-200"
          >
            {[
              { icon: Users, label: '900+ Learners' },
              { icon: Rocket, label: 'Apps Shipped to Play Store' },
              { icon: Award, label: 'Certificate + LinkedIn Credential' },
            ].map((item, i) => (
              <div key={item.label} className="flex items-center gap-2 text-gray-600 text-sm">
                <item.icon className="w-5 h-5 text-[#bebd19]" />
                <span>{item.label}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* PROBLEM SECTION */}
      <Section className="bg-gray-50 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-sm font-semibold text-[#bebd19] uppercase tracking-wider mb-3 block">Why Traditional Courses Fail</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Most coding courses leave you with tutorials, not apps.
            </h2>
            <p className="text-gray-500 text-lg leading-relaxed">
              They teach syntax without shipping, ignore the AI tools that make modern development 10x faster, and never help you launch. The result? A certificate with no Play Store listing.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-4"
          >
            {[
              { problem: 'No shipped apps', detail: 'Endless tutorials but nothing published to an actual store.' },
              { problem: 'No AI skills', detail: 'Courses that ignore Cursor AI, Copilot, and the tools that make devs productive.' },
              { problem: 'No placement support', detail: 'You finish the course and you\'re on your own to find work.' },
            ].map((item, i) => (
              <div key={i} className="flex gap-4 p-5 bg-white rounded-xl border border-gray-200">
                <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">{item.problem}</h4>
                  <p className="text-sm text-gray-500">{item.detail}</p>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </Section>

      {/* CURRICULUM */}
      <Section className="bg-white py-20" id="curriculum">
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold text-gray-900 mb-4"
          >
            14-Week Curriculum
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-gray-500 text-lg"
          >
            Six phases. From zero to published app — with AI pair-programming at every step.
          </motion.p>
        </div>

        <div className="max-w-4xl mx-auto space-y-4">
          {curriculum.map((phase, i) => (
            <motion.div
              key={phase.phase}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="bg-gray-50 border border-gray-200 rounded-xl overflow-hidden"
            >
              <div className="flex items-center justify-between px-6 py-4 bg-[#bebd19]/10 border-b border-gray-200">
                <h3 className="font-bold text-gray-900 text-lg">{phase.phase}</h3>
                <span className="text-xs font-semibold text-[#bebd19] bg-white px-3 py-1 rounded-full border border-[#bebd19]/20">{phase.weeks}</span>
              </div>
              {phase.modules.map((mod) => (
                <div key={mod.week} className="flex gap-5 px-6 py-4 border-b border-gray-200 last:border-b-0">
                  <span className="text-xs font-bold text-gray-400 pt-0.5 flex-shrink-0 w-14">{mod.week}</span>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm mb-1">{mod.title}</h4>
                    <p className="text-sm text-gray-500 leading-relaxed">{mod.desc}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          ))}
        </div>
      </Section>

      {/* AI TOOLS */}
      <Section className="bg-gray-50 py-20" id="tools">
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold text-gray-900 mb-4"
          >
            AI Tools You'll Master
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-gray-500 text-lg"
          >
            The exact stack used by modern app teams — taught where they genuinely accelerate development.
          </motion.p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
          {aiTools.map((tool, i) => (
            <motion.div
              key={tool.name}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="bg-white p-6 rounded-xl border border-gray-200 flex gap-4"
            >
              <div className="w-12 h-12 bg-[#bebd19]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <tool.icon className="w-6 h-6 text-[#bebd19]" />
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">{tool.name}</h4>
                <p className="text-sm text-gray-500 leading-relaxed">{tool.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* CAPSTONE PROJECTS */}
      <Section className="bg-white py-20">
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold text-gray-900 mb-4"
          >
            Capstone Projects
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-gray-500 text-lg"
          >
            Build a real, publishable app — the project that proves you can ship, not just code.
          </motion.p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {capstoneProjects.map((proj, i) => (
            <motion.div
              key={proj.title}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="bg-gray-50 border border-gray-200 rounded-xl p-6 hover:border-[#bebd19]/40 transition-colors"
            >
              <div className="w-10 h-10 bg-[#bebd19]/10 rounded-lg flex items-center justify-center mb-4">
                <Smartphone className="w-5 h-5 text-[#bebd19]" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{proj.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{proj.desc}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* WHO THIS IS FOR */}
      <Section className="bg-gray-50 py-20">
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold text-gray-900 mb-4"
          >
            Who This Is For
          </motion.h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {audience.map((a, i) => (
            <motion.div
              key={a.num}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="flex gap-4 bg-white p-6 rounded-xl border border-gray-200"
            >
              <div className="w-10 h-10 bg-[#bebd19] rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                {a.num}
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">{a.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{a.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* TESTIMONIALS */}
      <Section className="bg-white py-20">
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold text-gray-900 mb-4"
          >
            What Our Students Say
          </motion.h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {testimonials.map((t, i) => (
            <motion.div
              key={t.name}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="bg-gray-50 border border-gray-200 rounded-xl p-6"
            >
              <Quote className="w-8 h-8 text-[#bebd19]/30 mb-4" />
              <p className="text-gray-600 text-sm leading-relaxed mb-6 italic">"{t.text}"</p>
              <div>
                <p className="font-bold text-gray-900 text-sm">{t.name}</p>
                <p className="text-xs text-gray-500">{t.role}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* COMPARISON TABLE */}
      <Section className="bg-gray-50 py-20">
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold text-gray-900 mb-4"
          >
            How We Compare
          </motion.h2>
        </div>
        <div className="max-w-4xl mx-auto overflow-x-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-xl border border-gray-200 overflow-hidden min-w-[600px]"
          >
            <div className="grid grid-cols-4 text-sm font-bold text-gray-900 bg-gray-50 border-b border-gray-200">
              <div className="p-4">Feature</div>
              <div className="p-4 text-center text-[#bebd19]">PyrusMedia Academy</div>
              <div className="p-4 text-center">Udemy Courses</div>
              <div className="p-4 text-center">Typical Bootcamp</div>
            </div>
            {comparisonData.map((row, i) => (
              <div key={row.feature} className={`grid grid-cols-4 text-sm ${i < comparisonData.length - 1 ? 'border-b border-gray-200' : ''}`}>
                <div className="p-4 text-gray-700">{row.feature}</div>
                <div className="p-4 text-center">
                  {row.pyrus ? <CheckCircle2 className="w-5 h-5 text-green-500 mx-auto" /> : <svg className="w-5 h-5 text-gray-300 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>}
                </div>
                <div className="p-4 text-center">
                  {row.udemy ? <CheckCircle2 className="w-5 h-5 text-green-500 mx-auto" /> : <svg className="w-5 h-5 text-gray-300 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>}
                </div>
                <div className="p-4 text-center">
                  {row.bootcamp ? <CheckCircle2 className="w-5 h-5 text-green-500 mx-auto" /> : <svg className="w-5 h-5 text-gray-300 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </Section>

      {/* PRICING */}
      <Section className="bg-white py-20" id="pricing">
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold text-gray-900 mb-4"
          >
            One Price. Everything Included.
          </motion.h2>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-2xl mx-auto"
        >
          <div className="bg-gray-50 border border-gray-200 rounded-2xl overflow-hidden">
            <div className="bg-[#bebd19]/10 px-8 py-6 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900">App Development with AI — 14-Week Cohort</h3>
            </div>
            <div className="p-8">
              <ul className="space-y-3 mb-8">
                {[
                  '14 weeks live, 2 sessions per week',
                  'Flutter/React Native + AI tools training',
                  '1 real client-style capstone app',
                  'Small cohort — max 30 seats',
                  'Backend & API integration included',
                  'Certificate + LinkedIn credential',
                  'Access to session recordings',
                  'Play Store submission guidance',
                  'Placement support & freelance opportunities',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-gray-700">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="border-t border-gray-200 pt-6 mb-6">
                <p className="text-gray-500 text-sm mb-1">Course Fee</p>
                <div className="flex items-end gap-3">
                  <span className="text-4xl font-bold text-gray-900">₹35,000</span>
                  <span className="text-gray-500 text-sm pb-1">or No-cost EMI available</span>
                </div>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-lg px-4 py-3 mb-6">
                <p className="text-orange-700 text-sm font-semibold">⚠ Limited to 30 seats per batch — seats fill fast.</p>
              </div>
              <Button onClick={() => setShowModal(true)} className="w-full py-4 text-base">
                Reserve Your Seat — ₹35,000
              </Button>
            </div>
          </div>
        </motion.div>
      </Section>

      {/* FAQ */}
      <Section className="bg-gray-50 py-20" id="faq">
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold text-gray-900 mb-4"
          >
            Frequently Asked Questions
          </motion.h2>
        </div>
        <div className="max-w-3xl mx-auto space-y-3">
          {faqs.map((faq, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="bg-white border border-gray-200 rounded-xl overflow-hidden"
            >
              <button
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                className="w-full flex items-center justify-between px-6 py-5 text-left"
              >
                <span className="font-semibold text-gray-900 pr-4">{faq.q}</span>
                <ChevronDown
                  className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`}
                />
              </button>
              <AnimatePresence>
                {openFaq === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-5 text-sm text-gray-500 leading-relaxed border-t border-gray-200 pt-4">
                      {faq.a}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* FINAL CTA */}
      <Section className="bg-white py-24 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[150%] h-[150%] bg-[#bebd19]/5 blur-[120px] rounded-full pointer-events-none" />
        <div className="container mx-auto px-4 relative z-10 text-center max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Ready to Build Your First App?
            </h2>
            <p className="text-xl text-gray-500 mb-10">
              Reserve your seat today. Limited to 30 students per cohort.
            </p>
            <Button onClick={() => setShowModal(true)} className="px-10 py-4 text-lg">
              Reserve Your Seat
            </Button>
          </motion.div>
        </div>
      </Section>

      {/* ENROLLMENT MODAL */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          >
            <div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => !sending && setShowModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative bg-white rounded-2xl border border-gray-200 shadow-2xl w-full max-w-md p-8 max-h-[90vh] overflow-y-auto"
            >
              <button
                onClick={() => !sending && setShowModal(false)}
                disabled={sending}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-900 transition-colors disabled:opacity-50"
              >
                <X size={24} />
              </button>

              <h2 className="text-2xl font-bold text-gray-900 mb-2">Reserve Your Seat</h2>
              <p className="text-gray-500 text-sm mb-6">Fill in your details to reserve your spot in App Development with AI.</p>

              <div className="space-y-4">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleFormChange}
                    onBlur={handleFormBlur}
                    placeholder="Enter your full name"
                    className={inputClass('name')}
                  />
                  {errors.name && touched.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleFormChange}
                    onBlur={handleFormBlur}
                    placeholder="Enter your email"
                    className={inputClass('email')}
                  />
                  {errors.email && touched.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">Mobile Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleFormChange}
                    onBlur={handleFormBlur}
                    placeholder="Enter 10-digit mobile number"
                    className={inputClass('phone')}
                  />
                  {errors.phone && touched.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">Qualification *</label>
                  <select
                    name="qualification"
                    value={form.qualification}
                    onChange={handleFormChange}
                    onBlur={handleFormBlur}
                    className={inputClass('qualification')}
                  >
                    <option value="">Select your qualification</option>
                    <option>High School</option>
                    <option>Currently in College</option>
                    <option>Graduate</option>
                    <option>Post Graduate</option>
                    <option>Working Professional</option>
                  </select>
                  {errors.qualification && touched.qualification && <p className="text-red-500 text-xs mt-1">{errors.qualification}</p>}
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">Preferred Start Date *</label>
                  <select
                    value={selectedDate}
                    onChange={handleDateChange}
                    onBlur={handleDateBlur}
                    className={inputClass('date')}
                  >
                    <option value="">Select a date</option>
                    {batches.map((b, i) => (
                      <option key={i} value={b.date}>
                        {b.date} ({b.seatsLeft} seats left)
                      </option>
                    ))}
                  </select>
                  {errors.date && touched.date && <p className="text-red-500 text-xs mt-1">{errors.date}</p>}
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowModal(false)}
                  disabled={sending}
                  className="flex-1 py-3 rounded-full border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={sending}
                  className="flex-1 py-3 rounded-full bg-[#bebd19] text-black font-bold hover:bg-[#d4d325] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {sending ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Sending...
                    </>
                  ) : (
                    'Confirm & Continue'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* MOBILE STICKY CTA */}
      <AnimatePresence>
        {showMobileCta && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 px-4 py-3 md:hidden shadow-[0_-4px_20px_rgba(0,0,0,0.1)]"
          >
            <Button onClick={() => setShowModal(true)} className="w-full py-3 text-sm">
              Reserve Your Seat — ₹35,000
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AppDevelopment;
